// ما يلزم كلَّ نوع عملية من إثبات (قرار المالك) — **مصدرٌ واحد** لشاشتَي الإنشاء.
//
// «الدفعة إجبارية على الجديد والمكرر فقط. لو الحجز لشركة المهم يكتب اللي اتحصّل
// والمتبقي كام. وأما في التعديل لازم يرفع التحويل لينا برضو من العميل، وأما الإلغاء
// ينزل الشات بينه وبين الدعم اللي بيقول الغرامة أو المسترد كام».
//
// ولكل نوعٍ ما يثبته، ولا يُجبَر نوعٌ على إثباتِ غيره. والجُمل هنا لا في الشاشتين:
// شاشتان تكتبان نفس القاعدة بكلمتين مختلفتين تصيران قاعدتين في ذهن من يقرؤهما.

/** الأنواع التي تلزمها دفعة — نفس مجموعة الخادم حرفيًا (`requireSellingPayment`). */
export const PAYMENT_REQUIRED_OPS = new Set(['NEW', 'REPEAT', 'PRELIMINARY']);   // المبدئي بيعٌ بدفعته (0140)

/** جملةٌ تقول لصاحب الشاشة ما المطلوب في نوعه بعينه. */
export const PAYMENT_NOTE: Record<string, string> = {
  NEW: 'لازم تسجّل المحصَّل ولو جزئي، وترفع صورة إثباته. المتبقي بيتحسب لوحده.',
  REPEAT: 'لازم تسجّل المحصَّل ولو جزئي، وترفع صورة إثباته. المتبقي بيتحسب لوحده.',
  COMPANY: 'الشركة بتدفع باتفاقها — سجّل اللي اتحصّل فعلًا (لو في)، والمتبقي هيتحسب لوحده.',
  MODIFICATION: 'ارفع صورة التحويل اللي جالك من العميل مقابل التعديل.',
  CANCELLATION: 'ارفع صورة المحادثة مع الدعم اللي مكتوب فيها الغرامة أو المبلغ المسترد.',
};

/**
 * **مرفقات الحجز إجبارية** (قرار المالك: «إثبات الدفع إجباري ومرفقات الحجز كمان»):
 * لا يُحفظ حجزٌ بلا مستندٍ واحد على الأقل. وحجزٌ بلا ورقةٍ واحدة تسنده هو حجزٌ لا
 * يُراجَع ولا يُدافَع عنه عند خلاف — والورق يُجمع وقت البيع لا بعد شهر.
 */
export const ATTACHMENT_REQUIRED_MSG =
  'مرفقات الحجز إجبارية — ارفع صورة جواز السفر والتذكرة والشات اللي بينك وبين العميل؛ ده حقّك لو حصل خلاف.';

/** المستند الذي يلزم نوعًا بعينه — بصنفه، فيُعرف أنه رُفع لا أن شيئًا رُفع. */
export interface RequiredAttachment { category: string; ar: string; msg: string }

export const REQUIRED_ATTACHMENT: Record<string, RequiredAttachment> = {
  MODIFICATION: {
    category: 'MODIFICATION_TRANSFER_PROOF',
    ar: 'تحويل العميل مقابل التعديل',
    msg: 'حجز التعديل لازمه صورة التحويل اللي جالك من العميل — اختر صنف «تحويل العميل مقابل التعديل» في المرفقات.',
  },
  CANCELLATION: {
    category: 'SUPPORT_CHAT_SCREENSHOT',
    ar: 'محادثة الدعم (الغرامة / المسترد)',
    msg: 'حجز الإلغاء لازمه صورة المحادثة مع الدعم اللي مكتوب فيها الغرامة أو المسترد — اختر صنف «محادثة الدعم (الغرامة / المسترد)» في المرفقات.',
  },
};

/**
 * رسالةُ ما ينقص المرفقات، أو null حين تكتمل. واحدةٌ لا قائمة: من ينقصه مستندان
 * يُقال له الأول، فإذا رفعه قيل له الثاني — أوضح من فقرةٍ تُقرأ مرّةً وتُنسى.
 */
export function missingAttachment(op: string, files: readonly { category: string }[]): string | null {
  if (!files.length) return ATTACHMENT_REQUIRED_MSG;
  const need = REQUIRED_ATTACHMENT[op];
  if (need && !files.some(f => f.category === need.category)) return need.msg;
  return null;
}

/**
 * جملةٌ تُكتب فوق قسم المرفقات — **حين يلزم مستندٌ بعينه وحده** (قرار المالك: «شيل
 * الجملة دي»). أما أن المرفقات إجبارية فتقوله النجمة الحمراء على العنوان وشاراتُ
 * المستندات تحتها؛ وجملةٌ ثالثة تقول نفس الشيء بكلماتٍ أخرى ضوضاء تُقرأ مرّةً وتُهمل.
 */
export function attachmentHint(op: string): string | null {
  const need = REQUIRED_ATTACHMENT[op];
  return need ? `مطلوب مع هذا النوع: «${need.ar}».` : null;
}
